import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { dateFromServer } from "@/lib/utils";
import { ListResourcesResponseContent } from "@/requests/libraries/list-resources.request";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Book, Eye, Paperclip2 } from "iconsax-react";

// The number of years to show in the edition column
const YEAR_LIST_COUNT = 3;

const tagsToYearList = (tags: string[]) => {
  const years = Array.from(
    new Set(
      tags
        .map((item) => item.match(/^editionyear::(\d{4})$/)?.[1])
        .filter(Boolean)
        .map(Number),
    ),
  ).sort((a, b) => b - a);

  // Show only the top YEAR_LIST_COUNT years
  const topYears = years.slice(0, YEAR_LIST_COUNT).map(String);

  const remainingYears = years.slice(YEAR_LIST_COUNT);

  if (remainingYears.length > 0) {
    // Add the remaining count to the top YEAR_LIST_COUNT years
    topYears.push(`+${remainingYears.length}`);
  }

  return {
    labels: topYears,
    remainingYears,
  };
};

export const columns: ColumnDef<ListResourcesResponseContent>[] = [
  {
    accessorKey: "name",
    header: "Nombre",
    meta: {
      className: "truncate max-w-px w-full",
    },
    cell: ({ row }) => {
      const name = row.original.name;

      return (
        <>
          {row.original.type === "FILE" ? (
            <Book className="mr-2 inline opacity-40" size="20" />
          ) : (
            <Paperclip2 className="mr-2 inline opacity-40" size="20" />
          )}
          {name}
        </>
      );
    },
  },
  {
    id: "source",
    header: "Fuente",
    cell: ({ row }) => {
      const label = row.original.type === "FILE" ? "Interno" : "Público";

      return <Badge variant="purple">{label}</Badge>;
    },
  },
  {
    accessorKey: "uploadDate",
    header: "Fecha de subida",
    cell: ({ row }) => {
      const uploadDate =
        row.getValue<ListResourcesResponseContent["uploadDate"]>("uploadDate");

      const date = dateFromServer(uploadDate);

      return <p className="text-nowrap">{format(date, "dd-MM-yyyy")}</p>;
    },
  },
  {
    id: "editions",
    header: "Edición",
    meta: {
      className: "inline-flex gap-1",
    },
    cell: ({ row }) => {
      const yearList = tagsToYearList(row.original.tags);

      return (
        <>
          {yearList.labels.map((label, index) =>
            yearList.remainingYears.length > 0 &&
            index === yearList.labels.length - 1 ? (
              <Tooltip key={index}>
                <TooltipTrigger>
                  <Badge key={index} variant="sky">
                    {label}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="inline-flex max-w-xs flex-wrap gap-1">
                  <p>Usado también en las ediciones:</p>
                  {yearList.remainingYears.map((label, index) => (
                    <Badge key={index} variant="sky">
                      {label}
                    </Badge>
                  ))}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Badge key={index} variant="sky">
                {label}
              </Badge>
            ),
          )}
        </>
      );
    },
  },
  {
    id: "actions",
    header: "",
    cell: () => {
      return <Eye size={18} color="#615CB7" className="mx-2" />;
    },
  },
];
